/**
 * Google Gemini provider adapter for ludicrous-gateway
 * Converts Anthropic API format to Gemini API format
 */

import { BaseProvider } from "./base-provider.js";
import type {
  ProviderRequestContext,
  StreamHandler,
} from "../types/providers.js";
import type { MessagesRequest, MessagesResponse, StopReason } from "../types/messages.js";
import type { ProviderConfig } from "../types/config.js";
import type { TextContent, ContentBlock } from "../types/messages.js";

/**
 * Available Gemini models
 */
export const GEMINI_MODELS = [
  { value: 'gemini-2.0-flash-exp', label: 'Gemini 2.0 Flash (Latest)' },
  { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro (Best quality)' },
  { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash (Fast)' },
  { value: 'gemini-1.5-flash-8b', label: 'Gemini 1.5 Flash 8B (Cheapest)' },
];

/**
 * Default model aliases mapping Claude models to Gemini models
 */
export const GEMINI_MODEL_ALIASES: Record<string, string> = {
  'claude-opus-4-5-20251101': 'gemini-2.0-flash-exp',
  'claude-sonnet-4-20251114': 'gemini-1.5-flash',
  'claude-3-5-haiku-20241022': 'gemini-1.5-flash-8b',
};

/**
 * Gemini content part
 */
interface GeminiPart {
  text?: string;
  inlineData?: {
    mimeType: string;
    data: string;
  };
  functionCall?: {
    name: string;
    args: Record<string, unknown>;
  };
  functionResponse?: {
    name: string;
    response: {
      content: string;
    };
  };
}

/**
 * Gemini content item
 */
interface GeminiContent {
  role: "user" | "model";
  parts: GeminiPart[];
}

/**
 * Gemini tool definition
 */
interface GeminiTool {
  functionDeclarations: Array<{
    name: string;
    description?: string;
    parameters?: {
      type: string;
      properties?: Record<string, unknown>;
      required?: string[];
    };
  }>;
}

/**
 * Gemini request format
 */
interface GeminiRequest {
  contents: GeminiContent[];
  systemInstruction?: {
    parts: Array<{ text: string }>;
  };
  generationConfig?: {
    temperature?: number;
    topP?: number;
    topK?: number;
    maxOutputTokens?: number;
    stopSequences?: string[];
  };
  tools?: GeminiTool[];
}

/**
 * Gemini response candidate
 */
interface GeminiCandidate {
  content: {
    parts: GeminiPart[];
    role: "model";
  };
  finishReason?: "STOP" | "MAX_TOKENS" | "SAFETY" | "RECITATION" | "OTHER";
  safetyRatings?: Array<{
    category: string;
    probability: string;
  }>;
}

/**
 * Gemini response format
 */
interface GeminiResponse {
  candidates?: GeminiCandidate[];
  promptFeedback?: {
    blockReason?: string;
    safetyRatings?: Array<{
      category: string;
      probability: string;
    }>;
  };
  usageMetadata?: {
    promptTokenCount?: number;
    candidatesTokenCount?: number;
    totalTokenCount?: number;
  };
}

/**
 * Google Gemini API provider
 */
export class GeminiProvider extends BaseProvider {
  constructor(config: ProviderConfig) {
    super(config);

    // Apply default model aliases if not overridden
    for (const [from, to] of Object.entries(GEMINI_MODEL_ALIASES)) {
      if (!this.modelAliases.has(from)) {
        this.modelAliases.set(from, to);
      }
    }
  }

  protected getDefaultBaseUrl(): string {
    return "https://generativelanguage.googleapis.com/v1beta";
  }

  /**
   * Extract API key from context headers
   */
  private getApiKey(context: ProviderRequestContext): string {
    // Check for x-goog-api-key header first
    const googApiKey = context.headers["x-goog-api-key"];
    if (googApiKey) {
      return googApiKey;
    }

    // Fall back to Authorization header (Bearer token format)
    const authHeader = context.headers["authorization"] || context.headers["Authorization"];
    if (authHeader?.startsWith("Bearer ")) {
      return authHeader.slice(7);
    }

    // Check for x-api-key header
    const xApiKey = context.headers["x-api-key"];
    if (xApiKey) {
      return xApiKey;
    }

    throw new Error("No API key found. Provide x-goog-api-key header or Authorization: Bearer <key>");
  }

  /**
   * Convert Anthropic content blocks to Gemini parts
   */
  private convertContentToParts(content: string | ContentBlock[]): GeminiPart[] {
    if (typeof content === "string") {
      return [{ text: content }];
    }

    const parts: GeminiPart[] = [];

    for (const block of content) {
      switch (block.type) {
        case "text":
          parts.push({ text: block.text });
          break;
        case "image":
          if (block.source.type === "base64") {
            parts.push({
              inlineData: {
                mimeType: block.source.media_type,
                data: block.source.data,
              },
            });
          }
          // URL images not directly supported by Gemini inline, skip for now
          break;
        case "tool_use":
          parts.push({
            functionCall: {
              name: block.name,
              args: block.input,
            },
          });
          break;
        case "tool_result":
          parts.push({
            functionResponse: {
              name: block.tool_use_id,
              response: {
                content: typeof block.content === "string"
                  ? block.content
                  : JSON.stringify(block.content),
              },
            },
          });
          break;
      }
    }

    return parts.length > 0 ? parts : [{ text: "" }];
  }

  /**
   * Convert Anthropic messages to Gemini format
   */
  private convertToGeminiFormat(
    request: MessagesRequest,
    _resolvedModel: string
  ): GeminiRequest {
    const geminiRequest: GeminiRequest = {
      contents: [],
    };

    // Add system instruction if present
    if (request.system) {
      const systemText =
        typeof request.system === "string"
          ? request.system
          : request.system
              .filter((b): b is TextContent => b.type === "text")
              .map((b) => b.text)
              .join("\n");

      geminiRequest.systemInstruction = {
        parts: [{ text: systemText }],
      };
    }

    // Convert messages
    for (const msg of request.messages) {
      const role: "user" | "model" = msg.role === "assistant" ? "model" : "user";
      const parts = this.convertContentToParts(msg.content);

      geminiRequest.contents.push({ role, parts });
    }

    // Add generation config
    geminiRequest.generationConfig = {};

    if (request.temperature !== undefined) {
      geminiRequest.generationConfig.temperature = request.temperature;
    }
    if (request.top_p !== undefined) {
      geminiRequest.generationConfig.topP = request.top_p;
    }
    if (request.top_k !== undefined) {
      geminiRequest.generationConfig.topK = request.top_k;
    }
    if (request.max_tokens !== undefined) {
      geminiRequest.generationConfig.maxOutputTokens = request.max_tokens;
    }
    if (request.stop_sequences && request.stop_sequences.length > 0) {
      geminiRequest.generationConfig.stopSequences = request.stop_sequences;
    }

    // Convert tools if present
    if (request.tools && request.tools.length > 0) {
      geminiRequest.tools = [{
        functionDeclarations: request.tools.map(tool => {
          const decl: {
            name: string;
            description?: string;
            parameters?: {
              type: string;
              properties?: Record<string, unknown>;
              required?: string[];
            };
          } = { name: tool.name };

          if (tool.description) {
            decl.description = tool.description;
          }

          if (tool.input_schema) {
            decl.parameters = { type: tool.input_schema.type };
            if (tool.input_schema.properties) {
              decl.parameters.properties = tool.input_schema.properties;
            }
            if (tool.input_schema.required) {
              decl.parameters.required = tool.input_schema.required;
            }
          }

          return decl;
        }),
      }];
    }

    return geminiRequest;
  }

  /**
   * Convert Gemini finish reason to Anthropic stop reason
   */
  private convertFinishReason(finishReason?: string): StopReason {
    switch (finishReason) {
      case "MAX_TOKENS":
        return "max_tokens";
      case "STOP":
      default:
        return "end_turn";
    }
  }

  /**
   * Convert Gemini response to Anthropic format
   */
  private convertFromGeminiFormat(
    response: GeminiResponse,
    model: string
  ): MessagesResponse {
    const content: ContentBlock[] = [];
    let stopReason: StopReason = "end_turn";

    if (response.candidates && response.candidates.length > 0) {
      const candidate = response.candidates[0];
      if (candidate) {
        stopReason = this.convertFinishReason(candidate.finishReason);

        if (candidate.content?.parts) {
          for (const part of candidate.content.parts) {
          if (part.text) {
            content.push({
              type: "text",
              text: part.text,
            });
          }
          if (part.functionCall) {
            content.push({
              type: "tool_use",
              id: `toolu_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
              name: part.functionCall.name,
              input: part.functionCall.args,
            });
          }
        }
        }
      }
    }

    // Ensure at least one content block
    if (content.length === 0) {
      content.push({ type: "text", text: "" });
    }

    return {
      id: `gemini-${Date.now()}`,
      type: "message",
      role: "assistant",
      content,
      model,
      stop_reason: stopReason,
      stop_sequence: null,
      usage: {
        input_tokens: response.usageMetadata?.promptTokenCount ?? 0,
        output_tokens: response.usageMetadata?.candidatesTokenCount ?? 0,
      },
    };
  }

  /**
   * Send a non-streaming request to Gemini
   */
  async sendRequest(
    request: MessagesRequest,
    context: ProviderRequestContext
  ): Promise<MessagesResponse> {
    const startTime = Date.now();

    try {
      const apiKey = this.getApiKey(context);
      const url = `${this.baseUrl}/models/${context.resolvedModel}:generateContent?key=${apiKey}`;
      const geminiRequest = this.convertToGeminiFormat(request, context.resolvedModel);

      this.logger.debug("Sending request to Gemini", {
        url: url.replace(apiKey, "***"),
        model: context.resolvedModel,
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        context.timeoutMs
      );

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(geminiRequest),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Gemini API error: ${response.status} - ${errorText}`);
      }

      const data = (await response.json()) as GeminiResponse;
      const latencyMs = Date.now() - startTime;

      this.recordRequest(true, latencyMs);
      this.logger.debug("Gemini request successful", { latencyMs });

      return this.convertFromGeminiFormat(data, context.resolvedModel);
    } catch (error) {
      const latencyMs = Date.now() - startTime;
      this.recordRequest(false, latencyMs);

      this.logger.error("Gemini request failed", {
        error: error instanceof Error ? error.message : "Unknown error",
        latencyMs,
      });

      throw error;
    }
  }

  /**
   * Send a streaming request to Gemini
   */
  async sendStreamingRequest(
    request: MessagesRequest,
    context: ProviderRequestContext,
    handler: StreamHandler
  ): Promise<void> {
    const startTime = Date.now();

    try {
      const apiKey = this.getApiKey(context);
      const url = `${this.baseUrl}/models/${context.resolvedModel}:streamGenerateContent?key=${apiKey}&alt=sse`;
      const geminiRequest = this.convertToGeminiFormat(request, context.resolvedModel);

      this.logger.debug("Starting streaming request to Gemini", {
        url: url.replace(apiKey, "***"),
        model: context.resolvedModel,
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        context.timeoutMs
      );

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(geminiRequest),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Gemini API error: ${response.status} - ${errorText}`);
      }

      if (!response.body) {
        throw new Error("No response body for streaming request");
      }

      // Send initial message_start event (Anthropic SSE format)
      handler.onEvent(
        "message_start",
        JSON.stringify({
          type: "message_start",
          message: {
            id: `gemini-${Date.now()}`,
            type: "message",
            role: "assistant",
            content: [],
            model: context.resolvedModel,
            stop_reason: null,
            stop_sequence: null,
            usage: { input_tokens: 0, output_tokens: 0 },
          },
        })
      );

      // Send content_block_start
      handler.onEvent(
        "content_block_start",
        JSON.stringify({
          type: "content_block_start",
          index: 0,
          content_block: { type: "text", text: "" },
        })
      );

      // Process stream from Gemini
      // Gemini can return either NDJSON or SSE depending on alt parameter
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let totalOutputTokens = 0;
      let finishReason: string | undefined;

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Handle SSE format (lines starting with "data: ")
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine) continue;

          // SSE format: "data: {...}"
          let jsonStr = trimmedLine;
          if (trimmedLine.startsWith("data: ")) {
            jsonStr = trimmedLine.slice(6);
          }

          // Skip non-JSON lines
          if (!jsonStr.startsWith("{")) continue;

          try {
            const data = JSON.parse(jsonStr) as GeminiResponse;

            if (data.candidates && data.candidates.length > 0) {
              const candidate = data.candidates[0];

              if (candidate) {
                if (candidate.finishReason) {
                  finishReason = candidate.finishReason;
                }

                if (candidate.content?.parts) {
                  for (const part of candidate.content.parts) {
                    if (part.text) {
                      // Convert to Anthropic content_block_delta format
                      handler.onEvent(
                        "content_block_delta",
                        JSON.stringify({
                          type: "content_block_delta",
                          index: 0,
                          delta: {
                            type: "text_delta",
                            text: part.text,
                          },
                        })
                      );
                    }

                    if (part.functionCall) {
                      // Handle function calls in streaming
                      handler.onEvent(
                        "content_block_delta",
                        JSON.stringify({
                          type: "content_block_delta",
                          index: 0,
                          delta: {
                            type: "input_json_delta",
                            partial_json: JSON.stringify({
                              name: part.functionCall.name,
                              args: part.functionCall.args,
                            }),
                          },
                        })
                      );
                    }
                  }
                }
              }
            }

            if (data.usageMetadata?.candidatesTokenCount) {
              totalOutputTokens = data.usageMetadata.candidatesTokenCount;
            }
          } catch {
            // Skip malformed lines
            this.logger.debug("Skipping malformed Gemini response line");
          }
        }
      }

      // Send final events
      handler.onEvent(
        "content_block_stop",
        JSON.stringify({
          type: "content_block_stop",
          index: 0,
        })
      );

      handler.onEvent(
        "message_delta",
        JSON.stringify({
          type: "message_delta",
          delta: {
            stop_reason: this.convertFinishReason(finishReason),
            stop_sequence: null,
          },
          usage: {
            output_tokens: totalOutputTokens,
          },
        })
      );

      handler.onEvent(
        "message_stop",
        JSON.stringify({
          type: "message_stop",
        })
      );

      const latencyMs = Date.now() - startTime;
      this.recordRequest(true, latencyMs);
      handler.onComplete();

      this.logger.debug("Gemini streaming request completed", { latencyMs });
    } catch (error) {
      const latencyMs = Date.now() - startTime;
      this.recordRequest(false, latencyMs);

      const err = error instanceof Error ? error : new Error("Unknown error");
      this.logger.error("Gemini streaming request failed", {
        error: err.message,
        latencyMs,
      });

      handler.onError(err);
    }
  }

  /**
   * Health check for Gemini API
   */
  override async healthCheck(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      // Just check if the API endpoint is reachable
      // Note: This won't fully verify API key validity without making a real request
      const response = await fetch(`${this.baseUrl}/models`, {
        method: "GET",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // 401/403 means endpoint is reachable but needs auth (which is expected)
      const isHealthy = response.ok || response.status === 401 || response.status === 403;
      this.recordHealthCheck(isHealthy);
      return isHealthy;
    } catch (error) {
      this.recordHealthCheck(
        false,
        error instanceof Error ? error.message : "Unknown error"
      );
      return false;
    }
  }
}

/**
 * Create a Gemini provider from config
 */
export function createGeminiProvider(config: ProviderConfig): GeminiProvider {
  return new GeminiProvider(config);
}
